// Bulk tuition generation client — wraps rpc_bulk_generate_tuition.

import { supabase, isSupabaseConfigured } from "./supabase";

/**
 * @param params
 *  - cycleMonth: Date | string (any day inside the target month)
 *  - issueDate, dueDate: Date | string | null
 *  - classroomIds: uuid[] | null
 *  - familyIds: uuid[] | null
 *  - force: boolean   (void + regenerate existing live invoice)
 *  - commit: boolean  (false = dry-run preview)
 */
export async function runBulkTuition({
  cycleMonth = null,
  cycleWeek = null,
  cadence = "monthly",
  issueDate = null,
  dueDate = null,
  classroomIds = null,
  familyIds = null,
  force = false,
  commit = true,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_bulk_generate_tuition", {
    p_cycle_month: cycleMonth ? toDateStr(cycleMonth) : null,
    p_issue_date: issueDate ? toDateStr(issueDate) : null,
    p_due_date: dueDate ? toDateStr(dueDate) : null,
    p_classroom_ids: classroomIds && classroomIds.length ? classroomIds : null,
    p_family_ids: familyIds && familyIds.length ? familyIds : null,
    p_force: !!force,
    p_commit: !!commit,
    p_cadence: cadence,
    p_cycle_week: cycleWeek ? toDateStr(cycleWeek) : null,
  });
  if (error) throw error;
  return (data || []).map((row) => ({
    family_id:          row.out_family_id ?? row.family_id ?? null,
    billing_profile_id: row.out_billing_profile_id ?? row.billing_profile_id ?? null,
    family_name:        row.out_family_name ?? row.family_name ?? "",
    child_count:        row.out_child_count ?? row.child_count ?? 0,
    amount_cents:       row.out_amount_cents ?? row.amount_cents ?? 0,
    status:             row.out_status ?? row.status ?? "skipped",
    reason:             row.out_reason ?? row.reason ?? null,
    invoice_id:         row.out_invoice_id ?? row.invoice_id ?? null,
    voided_invoice_id:  row.out_voided_invoice_id ?? row.voided_invoice_id ?? null,
    sibling_discount_percent:
      row.out_sibling_discount_percent ?? row.sibling_discount_percent ?? 0,
    sibling_discount_cents:
      row.out_sibling_discount_cents ?? row.sibling_discount_cents ?? 0,
    cadence:            row.out_cadence ?? row.cadence ?? cadence,
  }));
}

export async function listClassroomsForOrg() {
  if (!isSupabaseConfigured) return [];
  const tryFetch = async (cols) =>
    supabase.from("classrooms").select(cols).order("name", { ascending: true });
  let { data, error } = await tryFetch(
    "id, name, age_group, monthly_tuition_cents, weekly_tuition_cents",
  );
  if (error && /column .* does not exist/i.test(error.message || "")) {
    ({ data, error } = await tryFetch(
      "id, name, age_group, monthly_tuition_cents",
    ));
  }
  if (error) throw error;
  return data || [];
}

function toDateStr(input) {
  if (!input) return null;
  if (typeof input === "string") return input.slice(0, 10);
  const d = input instanceof Date ? input : new Date(input);
  return d.toISOString().slice(0, 10);
}

// --- Display helpers ------------------------------------------------------

export const RESULT_STATUS_META = {
  created: {
    label: "Created",
    tone: "bg-primary/10 text-primary-hover border-primary/30",
    isHappy: true,
  },
  voided_and_regenerated: {
    label: "Regenerated",
    tone: "bg-secondary/30 text-neutral-900 border-secondary/50",
    isHappy: true,
  },
  would_create: {
    label: "Preview · will create",
    tone: "bg-primary/10 text-primary-hover border-primary/30",
    isHappy: true,
  },
  would_void_and_regenerate: {
    label: "Preview · will regenerate",
    tone: "bg-secondary/30 text-neutral-900 border-secondary/50",
    isHappy: true,
  },
  skipped: {
    label: "Skipped",
    tone: "bg-neutral-100 text-neutral-500 border-neutral-200",
    isHappy: false,
  },
};

export const SKIP_REASON_LABELS = {
  billing_profile_paused: "Billing profile is paused",
  billing_profile_closed: "Billing profile is closed",
  no_active_children: "No active, classroom-assigned children",
  no_children_in_selected_classrooms: "No matching children in selected classrooms",
  no_tuition_configured: "Tuition amount not configured",
  duplicate_draft: "Draft invoice already exists",
  duplicate_issued: "Issued invoice already exists",
  duplicate_paid: "Invoice already paid",
  duplicate_failed: "Failed invoice still open",
};

export function humanReason(reason) {
  if (!reason) return null;
  return SKIP_REASON_LABELS[reason] || reason;
}

export function groupResults(rows) {
  const g = {
    created: [],
    regenerated: [],
    skipped: [],
    wouldCreate: [],
    wouldRegenerate: [],
  };
  for (const r of rows) {
    if (r.status === "created") g.created.push(r);
    else if (r.status === "voided_and_regenerated") g.regenerated.push(r);
    else if (r.status === "would_create") g.wouldCreate.push(r);
    else if (r.status === "would_void_and_regenerate") g.wouldRegenerate.push(r);
    else g.skipped.push(r);
  }
  return g;
}
